import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Alerta, Badge, Boton, Busqueda, Campo, Cargando, Seleccion, Tabla, useFilaEnlace } from '../../components/kit'
import { DialogoRecinto } from '../../components/inventario/DialogoRecinto'
import { useAuth } from '../../contexts/AuthProvider'
import { useRecintos } from '../../queries/useInventario'
import { EstadoPlancheta, Sector } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { formatoFecha } from '../../utils/fecha'
import { PLANCHETA, SECTOR } from '../../utils/inventario'

/** Las oficinas y salas con el estado de su plancheta: el tablero de la revisión. */
export default function Oficinas() {
  const admin = useAuth().puede('administra_bienes')
  const recintos = useRecintos()
  const navigate = useNavigate()
  const fila = useFilaEnlace() // toda la fila lleva a la oficina
  const [q, setQ] = useState('')
  const [sector, setSector] = useState<Sector | ''>('')
  const [estado, setEstado] = useState<EstadoPlancheta | ''>('')
  const [creando, setCreando] = useState(false)

  const filtrados = useMemo(() => {
    const texto = q.trim().toUpperCase()
    return (recintos.data ?? []).filter(
      (r) =>
        r.activo &&
        (!texto || `${r.codigo} ${r.nombre} ${r.encargado?.nombre ?? ''}`.toUpperCase().includes(texto)) &&
        (!sector || r.sector === sector) &&
        (!estado || r.plancheta_estado === estado),
    )
  }, [recintos.data, q, sector, estado])

  return (
    <div className="pila pila--amplia">
      <header className="pagina__cabecera pagina__cabecera--con-accion">
        <div>
          <h1 className="dcc-t-heading-m">Oficinas</h1>
          <p className="texto-secundario">Cada oficina se revisa contra lo que dice el sistema y se imprime su plancheta.</p>
        </div>
        {admin && <Boton icono={Plus} variante="outlined" onClick={() => setCreando(true)}>Nueva oficina</Boton>}
      </header>

      <div className="filtros">
        <Busqueda etiqueta="Buscar oficina" placeholder="Oficina, código o encargado" value={q} onChange={(e) => setQ(e.target.value)} />
        <Campo etiqueta="Sector" ancho={false}>
          <Seleccion value={sector} onChange={(e) => setSector(e.target.value as Sector | '')}>
            <option value="">Todos</option>
            {(Object.keys(SECTOR) as Sector[]).map((s) => (
              <option key={s} value={s}>{SECTOR[s]}</option>
            ))}
          </Seleccion>
        </Campo>
        <Campo etiqueta="Plancheta" ancho={false}>
          <Seleccion value={estado} onChange={(e) => setEstado(e.target.value as EstadoPlancheta | '')}>
            <option value="">Todas</option>
            {(Object.keys(PLANCHETA) as EstadoPlancheta[]).map((p) => (
              <option key={p} value={p}>{PLANCHETA[p].texto}</option>
            ))}
          </Seleccion>
        </Campo>
        {recintos.data && <span className="filtros__cuenta">{filtrados.length} oficinas</span>}
      </div>

      {recintos.isLoading && <Cargando />}
      {recintos.isError && <Alerta tipo="error">{mensajeDeError(recintos.error)}</Alerta>}
      {recintos.data && !filtrados.length && <p className="texto-secundario">Ninguna oficina calza con el filtro.</p>}
      {filtrados.length > 0 && (
        <Tabla etiqueta="Oficinas" apilable>
          <thead>
            <tr>
              <th scope="col">Oficina</th>
              <th scope="col">Ubicación</th>
              <th scope="col">Encargado</th>
              <th scope="col" className="num">Bienes</th>
              <th scope="col" className="num">No ubicados</th>
              <th scope="col">Plancheta</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map((r) => (
              <tr key={r.codigo} {...fila(`/app/oficinas/${r.codigo}`)}>
                <td className="tabla__principal tabla__texto">
                  <Link to={`/app/oficinas/${r.codigo}`} className="tabla__enlace">{r.nombre}</Link>
                  <span className="tabla__sub">{r.codigo}</span>
                </td>
                <td data-etiqueta="Ubicación">{SECTOR[r.sector]}{r.piso != null ? ` · piso ${r.piso}` : ''}</td>
                <td data-etiqueta="Encargado" className="tabla__texto">{r.encargado?.nombre ?? <span className="texto-secundario">Sin asignar</span>}</td>
                <td data-etiqueta="Bienes" className="num">{r.n_bienes}</td>
                <td data-etiqueta="No ubicados" className="num">{r.n_no_ubicados || '—'}</td>
                <td data-etiqueta="Plancheta">
                  <div>
                    <Badge tono={PLANCHETA[r.plancheta_estado].tono} punto>{PLANCHETA[r.plancheta_estado].texto}</Badge>
                    <span className="tabla__sub">desde {formatoFecha(r.plancheta_desde)}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}

      {creando && <DialogoRecinto onCerrar={() => setCreando(false)} onCreado={(r) => navigate(`/app/oficinas/${r.codigo}`)} />}
    </div>
  )
}
